import { t, Trans } from "@lingui/macro";
import { Box, Menu, MenuItem, Container } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import useEditMaps from "./useEditMaps";
import useMaps from "./useMaps";
import useNavUpOnDeviceDisconnect from "../useNavUpOnDeviceDisconnect";
import MapsEmbed from "./MapsEmbed";
import { WfIconNavigationOverflowRegular } from "@WahooFitness/redesignr";
import { GlobalHeader } from "@WahooFitness/wahoo-offline-mfe";

const Maps = ({ appToken }: { appToken: string }) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchorEl);

  const openMenu = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  }, []);

  const closeMenu = useCallback(() => {
    setMenuAnchorEl(null);
  }, []);

  useNavUpOnDeviceDisconnect(appToken);

  const { mapsSynced, mapPack, mutateMapPack, addMapUpdateListener, removeMapUpdateListener } =
    useMaps(appToken);

  useEffect(() => {
    if (!mapsSynced) return;
    addMapUpdateListener("Maps", () => {
      mutateMapPack();
    });
    return () => removeMapUpdateListener("Maps");
  }, [addMapUpdateListener, mapsSynced, mutateMapPack, removeMapUpdateListener]);

  const tilePacks = useMemo(() => mapPack?.tilePacks, [mapPack]);
  const {
    editing,
    onDeleteClick,
    disableEditing,
    enableEditing,
    handleMapSelect,
    pendingDeleteTilePackIds,
  } = useEditMaps(appToken, tilePacks || []);

  const handleEditClick = useCallback(() => {
    setMenuAnchorEl(null);
    enableEditing();
  }, [enableEditing]);

  const navigate = useNavigate();
  const navigateToUpdatePage = useCallback(() => {
    navigate(`/device-config/${appToken}/maps/update`);
  }, [appToken, navigate]);

  const handleUpdateAllClick = useCallback(() => {
    navigateToUpdatePage();
    closeMenu();
  }, [closeMenu, navigateToUpdatePage]);

  return (
    <>
      <GlobalHeader
        title={t`Manage maps`}
        primaryRightActionItem={
          editing
            ? {
                text: t`Delete`,
                action: onDeleteClick,
              }
            : {
                icon: <WfIconNavigationOverflowRegular />,
                action: openMenu,
              }
        }
        primaryLeftActionItem={
          editing
            ? {
                text: t`Done`,
                action: disableEditing,
              }
            : undefined
        }
      />
      <Container maxWidth="sm" disableGutters sx={{ minHeight: "100%" }}>
        <Box
          width="100%"
          height="100%"
          display="flex"
          flexDirection="column"
          p={2}
          justifyContent="space-between"
        >
          <MapsEmbed
            appToken={appToken}
            onUpdateAllClick={navigateToUpdatePage}
            editing={editing}
            onMapSelect={handleMapSelect}
            pendingDeleteTilePackIds={pendingDeleteTilePackIds}
          />
          <Menu open={menuOpen} onClose={closeMenu} anchorEl={menuAnchorEl}>
            <MenuItem onClick={handleEditClick}>
              <Trans>Edit</Trans>
            </MenuItem>
            <MenuItem onClick={handleUpdateAllClick}>
              <Trans>Update maps</Trans>
            </MenuItem>
          </Menu>
        </Box>
      </Container>
    </>
  );
};

export default Maps;
